import React from 'react';

function clampNumber(value, fallback = 0) {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function parsePx(value, fallback) {
  if (value === undefined || value === null) return fallback;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const match = value.match(/-?\d+(\.\d+)?/);
    if (match) {
      const num = Number(match[0]);
      return Number.isFinite(num) ? num : fallback;
    }
  }
  return fallback;
}

function getChildOfType(children, component) {
  return React.Children.toArray(children).find((child) => React.isValidElement(child) && child.type === component);
}

function getChildrenOfType(children, component) {
  return React.Children.toArray(children).filter((child) => React.isValidElement(child) && child.type === component);
}

function asCssStrokeDasharray(value) {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.join(' ');
  return undefined;
}

function getText(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return String(value);
}

function niceTicks(min, max, count) {
  // Basic linear tick generator; "nice enough" for our calculator charts.
  const span = Math.max(1e-9, max - min);
  const rawStep = span / Math.max(1, count - 1);
  const pow10 = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const err = rawStep / pow10;
  let step = pow10;
  if (err >= 7.5) step = 10 * pow10;
  else if (err >= 3.5) step = 5 * pow10;
  else if (err >= 1.5) step = 2 * pow10;
  const niceMin = Math.floor(min / step) * step;
  const niceMax = Math.ceil(max / step) * step;
  const ticks = [];
  for (let v = niceMin; v <= niceMax + step * 0.5; v += step) ticks.push(v);
  return ticks;
}

function formatTooltipValue(formatter, value, name, payload) {
  if (typeof formatter !== 'function') return { value: getText(value), name };
  const out = formatter(value, name, payload);
  if (Array.isArray(out)) return { value: getText(out[0]), name: out[1] ?? name };
  return { value: getText(out), name };
}

// Minimal, dependency-free fallback for the small set of Recharts components used in this codebase.
// It intentionally implements only the props/features we rely on, to avoid pulling a full charting lib.

export function ResponsiveContainer({ width = '100%', height = '100%', children }) {
  return (
    <div style={{ width, height }}>
      {children}
    </div>
  );
}

export function LineChart({ data = [], children }) {
  const [hover, setHover] = React.useState(null);
  const svgRef = React.useRef(null);
  const [chartSize, setChartSize] = React.useState({ width: 1000, height: 360 });

  const lines = getChildrenOfType(children, Line).map((el) => el.props);
  const xAxis = getChildOfType(children, XAxis)?.props;
  const yAxis = getChildOfType(children, YAxis)?.props;
  const grid = getChildOfType(children, CartesianGrid)?.props;
  const tooltip = getChildOfType(children, Tooltip)?.props;
  const legend = getChildOfType(children, Legend)?.props;

  React.useLayoutEffect(() => {
    const el = svgRef.current;
    if (!el) return undefined;

    const update = () => {
      const rect = el.getBoundingClientRect();
      if (!rect || rect.width <= 0 || rect.height <= 0) return;
      const nextW = Math.round(rect.width);
      const nextH = Math.round(rect.height);
      setChartSize((prev) => (prev.width === nextW && prev.height === nextH ? prev : { width: nextW, height: nextH }));
    };

    update();

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => ro.disconnect();
    }

    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  if (!Array.isArray(data) || data.length < 2 || lines.length === 0) {
    return <div className="w-full h-full" />;
  }

  const hasLegend = Boolean(legend);
  const width = Math.max(320, clampNumber(chartSize.width, 1000));
  const height = Math.max(220, clampNumber(chartSize.height, 360));
  const margin = {
    top: 20,
    right: 20,
    bottom: hasLegend ? 78 : 40,
    left: 60,
  };
  const plotW = Math.max(1, width - margin.left - margin.right);
  const plotH = Math.max(1, height - margin.top - margin.bottom);

  const values = [];
  for (const point of data) {
    for (const line of lines) {
      values.push(clampNumber(point?.[line.dataKey], 0));
    }
  }
  const rawMaxY = Math.max(1, ...values);
  const rawMinY = Math.min(...values);
  const allNonNegative = values.every((v) => v >= 0);
  const pad = (rawMaxY - rawMinY) * 0.06;
  const maxY = rawMaxY + (Number.isFinite(pad) ? pad : 0);
  const minY = allNonNegative ? 0 : rawMinY - (Number.isFinite(pad) ? pad : 0);
  const spanY = Math.max(1, maxY - minY);

  const toX = (i) => margin.left + (i * plotW) / (data.length - 1);
  const toY = (v) => {
    const t = (clampNumber(v, 0) - minY) / spanY;
    return margin.top + (1 - t) * plotH;
  };

  const xKey = xAxis?.dataKey;
  const xLabelForIndex = (i) => (xKey ? data?.[i]?.[xKey] : i + 1);

  const yTicks = niceTicks(minY, maxY, 5);
  const yFormatter = typeof yAxis?.tickFormatter === 'function' ? yAxis.tickFormatter : (v) => getText(v);

  const gridStroke = grid?.stroke || 'rgba(255,255,255,0.10)';
  const gridDash = asCssStrokeDasharray(grid?.strokeDasharray) || '3 3';

  const pickIndexFromClientX = (clientX, svg) => {
    const rect = svg.getBoundingClientRect();
    const px = clientX - rect.left;
    const t = (px - (rect.width * margin.left) / width) / ((rect.width * plotW) / width);
    const i = Math.round(t * (data.length - 1));
    return Math.max(0, Math.min(data.length - 1, i));
  };

  const onMove = (e) => {
    const svg = e.currentTarget;
    const idx = pickIndexFromClientX(e.clientX, svg);
    setHover({ index: idx });
  };

  const onLeave = () => setHover(null);

  const hoverIndex = hover?.index ?? null;
  const hoverX = hoverIndex === null ? null : toX(hoverIndex);
  const tooltipFormatter = tooltip?.formatter;
  const tooltipLabel = hoverIndex === null ? '' : getText(xLabelForIndex(hoverIndex));

  const legendItems = lines.map((line) => ({
    name: getText(line.name || line.dataKey),
    color: line.stroke || '#F5A623',
  }));

  const legendLayout = legend && legendItems.length > 0 ? (() => {
    const fontSize = Math.max(12, clampNumber(legend?.fontSize, 12));
    const rowH = Math.max(18, Math.round(fontSize * 1.5));
    const markerW = 14;
    const markerH = 8;
    const markerRx = 4;
    const itemGap = 18;
    const markerGap = 8;
    const textColor = '#E2E8F0';

    const estimateTextW = (text) => Math.round(getText(text).length * (fontSize * 0.56));
    const items = legendItems.map((it) => ({
      ...it,
      textW: estimateTextW(it.name),
    }));

    const maxRowW = Math.max(1, width - margin.left - margin.right);
    const rows = [];
    let current = { items: [], width: 0 };

    for (const it of items) {
      const itemW = markerW + markerGap + it.textW + itemGap;
      if (current.items.length > 0 && current.width + itemW > maxRowW) {
        rows.push(current);
        current = { items: [], width: 0 };
      }
      current.items.push(it);
      current.width += itemW;
    }
    if (current.items.length > 0) rows.push(current);

    // remove trailing gap
    for (const row of rows) row.width = Math.max(0, row.width - itemGap);

    const xAxisTickY = margin.top + plotH + 20;
    const startY = xAxisTickY + 22;

    return {
      fontSize,
      rowH,
      markerW,
      markerH,
      markerRx,
      markerGap,
      itemGap,
      textColor,
      rows,
      startY,
      maxRowW,
    };
  })() : null;

  return (
    <svg
      ref={svgRef}
      className="w-full h-full"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="Chart"
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      <defs>
        <filter id="chart-tooltip-shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="10" stdDeviation="10" floodColor="rgba(0,0,0,0.45)" />
        </filter>
      </defs>
      {/* Grid */}
      {grid && (
        <g>
          {yTicks.map((t) => {
            const y = toY(t);
            return (
              <line
                key={`gy-${t}`}
                x1={margin.left}
                x2={width - margin.right}
                y1={y}
                y2={y}
                stroke={gridStroke}
                strokeWidth="1"
                strokeDasharray={gridDash}
                vectorEffect="non-scaling-stroke"
              />
            );
          })}
        </g>
      )}

      {/* Axes */}
      <g>
        <line
          x1={margin.left}
          x2={width - margin.right}
          y1={margin.top + plotH}
          y2={margin.top + plotH}
          stroke="rgba(255,255,255,0.14)"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
        />
        <line
          x1={margin.left}
          x2={margin.left}
          y1={margin.top}
          y2={margin.top + plotH}
          stroke="rgba(255,255,255,0.14)"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
        />

        {/* Y ticks */}
        {yTicks.map((t) => {
          const y = toY(t);
          return (
            <g key={`yt-${t}`}>
              <line
                x1={margin.left - 6}
                x2={margin.left}
                y1={y}
                y2={y}
                stroke="rgba(255,255,255,0.14)"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
              <text
                x={margin.left - 10}
                y={y + 4}
                textAnchor="end"
                fontSize={yAxis?.fontSize ?? 11}
                fill={yAxis?.stroke || '#94a3b8'}
              >
                {yFormatter(t)}
              </text>
            </g>
          );
        })}

        {/* X ticks */}
        {(() => {
          const maxTicks = 6;
          const step = Math.max(1, Math.ceil(data.length / maxTicks));
          const ticks = [];
          for (let i = 0; i < data.length; i += step) ticks.push(i);
          if (ticks[ticks.length - 1] !== data.length - 1) ticks.push(data.length - 1);
          return ticks.map((i) => {
            const x = toX(i);
            const y = margin.top + plotH;
            return (
              <g key={`xt-${i}`}>
                <line
                  x1={x}
                  x2={x}
                  y1={y}
                  y2={y + 6}
                  stroke="rgba(255,255,255,0.14)"
                  strokeWidth="1"
                  vectorEffect="non-scaling-stroke"
                />
                <text
                  x={x}
                  y={y + 20}
                  textAnchor="middle"
                  fontSize={xAxis?.fontSize ?? 11}
                  fill={xAxis?.stroke || '#94a3b8'}
                >
                  {getText(xLabelForIndex(i))}
                </text>
              </g>
            );
          });
        })()}
      </g>

      {/* Series */}
      {lines.map((line, idx) => {
        const d = data
          .map((point, i) => `${i === 0 ? 'M' : 'L'} ${toX(i)} ${toY(point?.[line.dataKey])}`)
          .join(' ');

        return (
          <path
            // eslint-disable-next-line react/no-array-index-key
            key={idx}
            d={d}
            fill="none"
            stroke={line.stroke || '#F5A623'}
            strokeWidth={line.strokeWidth ?? 2}
            strokeDasharray={line.strokeDasharray}
            vectorEffect="non-scaling-stroke"
          />
        );
      })}

      {/* Legend */}
      {legendLayout && (
        <g pointerEvents="none">
          {legendLayout.rows.map((row, rowIdx) => {
            const rowW = Math.min(legendLayout.maxRowW, row.width);
            const startX = margin.left + (plotW - rowW) / 2;
            const y = legendLayout.startY + rowIdx * legendLayout.rowH;
            let x = startX;

            return (
              <g key={`lr-${rowIdx}`}>
                {row.items.map((it, itemIdx) => {
                  const markerY = y - legendLayout.markerH + 2;
                  const textX = x + legendLayout.markerW + legendLayout.markerGap;
                  const itemW = legendLayout.markerW + legendLayout.markerGap + it.textW + legendLayout.itemGap;

                  const el = (
                    <g key={`lg-${rowIdx}-${itemIdx}`}>
                      <rect
                        x={x}
                        y={markerY}
                        width={legendLayout.markerW}
                        height={legendLayout.markerH}
                        rx={legendLayout.markerRx}
                        ry={legendLayout.markerRx}
                        fill={it.color}
                        opacity="0.95"
                      />
                      <text
                        x={textX}
                        y={y}
                        fontSize={legendLayout.fontSize}
                        fill={legendLayout.textColor}
                      >
                        {it.name}
                      </text>
                    </g>
                  );

                  x += itemW;
                  return el;
                })}
              </g>
            );
          })}
        </g>
      )}

      {/* Hover markers + tooltip */}
      {tooltip && hoverIndex !== null && hoverX !== null && (
        <g>
          <line
            x1={hoverX}
            x2={hoverX}
            y1={margin.top}
            y2={margin.top + plotH}
            stroke={tooltip?.cursor?.stroke || 'rgba(255,255,255,0.12)'}
            strokeWidth={parsePx(tooltip?.cursor?.strokeWidth, 1)}
            vectorEffect="non-scaling-stroke"
          />
          {lines.map((line, idx) => {
            const y = toY(data?.[hoverIndex]?.[line.dataKey]);
            return (
              <circle
                // eslint-disable-next-line react/no-array-index-key
                key={`hc-${idx}`}
                cx={hoverX}
                cy={y}
                r="3.5"
                fill={line.stroke || '#F5A623'}
                stroke="rgba(0,0,0,0.35)"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
            );
          })}

          {(() => {
            const rows = lines.map((line) => {
              const raw = data?.[hoverIndex]?.[line.dataKey];
              const name = line.name || line.dataKey;
              return { ...formatTooltipValue(tooltipFormatter, raw, name, data?.[hoverIndex]), color: line.stroke || '#F5A623' };
            });

            const contentStyle = tooltip?.contentStyle || {};
            const labelStyle = tooltip?.labelStyle || {};
            const itemStyle = tooltip?.itemStyle || {};

            const baseFontSize = Math.max(14, parsePx(contentStyle.fontSize, 14));
            const rowH = Math.max(20, Math.round(baseFontSize * 1.45));
            const pad = Math.max(12, parsePx(contentStyle.padding, 14));
            const preferLeft = hoverX > width * 0.62;
            const fill = contentStyle.backgroundColor || 'rgba(2,6,23,0.92)';
            const stroke = (contentStyle.border && typeof contentStyle.border === 'string')
              ? contentStyle.border.replace(/^1px\s+solid\s+/i, '')
              : 'rgba(255,255,255,0.16)';
            const rx = parsePx(contentStyle.borderRadius, 12);
            const labelFill = labelStyle.color || '#FBBF24';
            const labelFontSize = Math.max(13, parsePx(labelStyle.fontSize, baseFontSize - 1));
            const itemFontSize = Math.max(13, parsePx(itemStyle.fontSize, baseFontSize));
            const itemFill = itemStyle.color || '#F8FAFC';

            const estimateTextW = (text, size) => Math.round(getText(text).length * (size * 0.58));
            const rowTextOffsetX = 14; // marker + gap before row text
            const rowStrings = rows.map((r) => `${getText(r.name)}: ${getText(r.value)}`);
            const maxRowTextW = Math.max(0, ...rowStrings.map((s) => estimateTextW(s, itemFontSize)));
            const labelW = estimateTextW(tooltipLabel, labelFontSize);

            const computedW = Math.max(
              pad * 2 + labelW,
              pad * 2 + rowTextOffsetX + maxRowTextW,
            );
            const boxW = clamp(parsePx(contentStyle.width, 0) || computedW, 170, 520);

            const labelLineH = Math.max(18, Math.round(labelFontSize * 1.25));
            const afterLabelGap = 6;
            const firstRowBaseline = labelLineH + afterLabelGap + itemFontSize;
            const contentBottomY = rows.length
              ? firstRowBaseline + (rows.length - 1) * rowH + Math.round(itemFontSize * 0.45)
              : labelLineH;
            const boxH = pad + contentBottomY + pad;

            const bx = preferLeft
              ? Math.max(margin.left + 8, hoverX - boxW - 12)
              : Math.min(width - margin.right - boxW - 8, Math.max(margin.left + 8, hoverX + 12));
            const by = clamp(margin.top + 10, margin.top + 8, height - margin.bottom - boxH - 8);

            return (
              <g pointerEvents="none">
                <rect
                  x={bx}
                  y={by}
                  width={boxW}
                  height={boxH}
                  rx={rx}
                  ry={rx}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth="1"
                  filter="url(#chart-tooltip-shadow)"
                />
                <text x={bx + pad} y={by + pad + Math.round(labelFontSize)} fontSize={labelFontSize} fill={labelFill}>
                  {tooltipLabel}
                </text>
                {rows.map((r, i) => (
                  <g key={`tr-${i}`}>
                    <circle cx={bx + pad} cy={by + pad + firstRowBaseline + i * rowH - Math.round(itemFontSize * 0.35)} r="4" fill={r.color} />
                    <text
                      x={bx + pad + rowTextOffsetX}
                      y={by + pad + firstRowBaseline + i * rowH}
                      fontSize={itemFontSize}
                      fill={itemFill}
                    >
                      {r.name}: {r.value}
                    </text>
                  </g>
                ))}
              </g>
            );
          })()}
        </g>
      )}
    </svg>
  );
}

export function PieChart({ children }) {
  const pie = getChildOfType(children, Pie);
  if (!pie) return <div className="w-full h-full" />;

  const { data = [], innerRadius = 0, outerRadius = 80, cx = '50%', cy = '50%', dataKey = 'value' } = pie.props;
  if (!Array.isArray(data) || data.length === 0) return <div className="w-full h-full" />;

  const width = 300;
  const height = 300;
  const centerX = typeof cx === 'string' && cx.endsWith('%') ? (parseFloat(cx) / 100) * width : clampNumber(cx, width / 2);
  const centerY = typeof cy === 'string' && cy.endsWith('%') ? (parseFloat(cy) / 100) * height : clampNumber(cy, height / 2);
  const rOuter = clampNumber(outerRadius, 80);
  const rInner = clampNumber(innerRadius, 0);

  const total = data.reduce((sum, d) => sum + clampNumber(d?.[dataKey], 0), 0) || 1;
  let startAngle = -Math.PI / 2;

  const arcPath = (a0, a1, r0, r1) => {
    const largeArc = a1 - a0 > Math.PI ? 1 : 0;
    const x0 = centerX + r1 * Math.cos(a0);
    const y0 = centerY + r1 * Math.sin(a0);
    const x1 = centerX + r1 * Math.cos(a1);
    const y1 = centerY + r1 * Math.sin(a1);
    const x2 = centerX + r0 * Math.cos(a1);
    const y2 = centerY + r0 * Math.sin(a1);
    const x3 = centerX + r0 * Math.cos(a0);
    const y3 = centerY + r0 * Math.sin(a0);

    return [
      `M ${x0} ${y0}`,
      `A ${r1} ${r1} 0 ${largeArc} 1 ${x1} ${y1}`,
      `L ${x2} ${y2}`,
      `A ${r0} ${r0} 0 ${largeArc} 0 ${x3} ${y3}`,
      'Z',
    ].join(' ');
  };

  return (
    <svg className="w-full h-full" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" role="img" aria-label="Chart">
      {data.map((slice, idx) => {
        const value = clampNumber(slice?.[dataKey], 0);
        const angle = (value / total) * Math.PI * 2;
        const endAngle = startAngle + angle;
        const d = arcPath(startAngle, endAngle, rInner, rOuter);
        startAngle = endAngle;

        return (
          <path
            // eslint-disable-next-line react/no-array-index-key
            key={idx}
            d={d}
            fill={slice?.color || '#F5A623'}
            stroke="rgba(255,255,255,0.08)"
            strokeWidth="1"
          />
        );
      })}
    </svg>
  );
}

// Below are “marker” components: they don't render, but let LineChart/PieChart
// inspect props from their children to draw the chart.
export function Line() {
  return null;
}
export function XAxis() {
  return null;
}
export function YAxis() {
  return null;
}
export function CartesianGrid() {
  return null;
}
export function Tooltip() {
  return null;
}
export function Legend() {
  return null;
}
export function ReferenceLine() {
  return null;
}
export function Pie() {
  return null;
}
export function Cell() {
  return null;
}
